// Game Configuration
export const CONFIG = {
  FOOD_PER_WORKER: 1,
  STARTING_FOOD: 10,
  STARTING_WOOD: 5,
  STARTING_STONE: 2,
  STARTING_METAL: 0,
  STARTING_KNOWLEDGE: 0,
  STARTING_MORALE: 50,
  STARTING_TECH_POINTS: 0,
  STARTING_WORKERS: 3,
  WIN_DAY: 50,
  BUILD_THRESHOLD: 10,
  PUSH_INJURY_THRESHOLD: 2,
  KNOWLEDGE_CAP: 100,
  MORALE_DEFECTION_THRESHOLD: 40,
  // New config for task system features
  CAVE_IN_THRESHOLD: 1,        // Mine task: natural 1 causes cave-in
  DISEASE_CHANCE_PER_DAY: 5,   // 5% base chance of disease outbreak per day
  TREAT_HEAL_CHANCE: 40,       // 40% chance to heal sick worker per treating worker
  SICK_WORKER_PRODUCTIVITY: 0, // Sick workers cannot work
  // Trade exchange rates — food(1) < wood(2) < stone(3) < metal(4)
  TRADE_EXCHANGE_RATES: {
    food:  { wood: 0.5,  stone: 0.33, metal: 0.25 },
    wood:  { food: 2,    stone: 0.67, metal: 0.5  },
    stone: { food: 3,    wood: 1.5,   metal: 0.67 },
    metal: { food: 4,    wood: 2,     stone: 1.5  }
  }
};

// Task types - generated from task definitions
import { TASK_DEFINITIONS } from './tasks.js';
export const TASKS = Object.keys(TASK_DEFINITIONS);

// Event definitions — full 20-entry d20 table
export const EVENTS = [
  { roll: [1, 2],   name: 'Raid NOW!',     icon: '⚔️', type: 'raid',     description: 'Raiders attack immediately!', surprise: true },
  { roll: [3, 4],   name: 'Raid Warning',  icon: '🏴', type: 'warning',  description: 'Raiders spotted! Attack tomorrow.' },
  { roll: [5, 5],   name: 'Storm',         icon: '⛈️', type: 'storm',    description: 'Outdoor tasks yield nothing today.' },
  { roll: [6, 6],   name: 'Spoilage',      icon: '🪱', type: 'spoilage', description: 'Some food has rotted.' },
  { roll: [7, 8],   name: 'Quiet Day',     icon: '☀️', type: 'quiet',    description: 'A peaceful day in the settlement.' },
  {
    roll: [9, 10],
    name: 'Traveler',
    icon: '🧳',
    type: 'traveler_choice',
    description: 'A wanderer offers to help. Will you invite them to stay permanently?',
    choices: [
      {
        id: 'temp',
        label: 'Accept Temporary Help',
        description: 'They work today and leave tomorrow (free)',
        effects: { tempWorker: true }
      },
      {
        id: 'recruit',
        label: 'Invite to Stay',
        description: 'Recruit them permanently',
        cost: { food: 5 },
        effects: { recruitTraveler: true, morale: 5 }
      },
      {
        id: 'decline',
        label: 'Politely Decline',
        description: 'Send them on their way',
        effects: { morale: -2 }
      }
    ]
  },
  {
    roll: [11, 11],
    name: 'Merchant',
    icon: '🏪',
    type: 'merchant',
    description: 'A merchant offers to trade resources.',
    choices: [
      {
        id: 'trade',
        label: 'Trade 10 food → 5 wood + 3 stone',
        description: 'Exchange food for building materials',
        cost: { food: 10 },
        effects: { wood: 5, stone: 3, message: 'The merchant makes a fair trade.' }
      },
      {
        id: 'decline',
        label: 'Decline',
        description: 'Send the merchant on their way',
        effects: { message: 'The merchant tips their hat and leaves.' }
      }
    ]
  },
  {
    roll: [12, 12],
    name: 'Refugees',
    icon: '🚶',
    type: 'refugees',
    description: 'Refugees seek shelter in your settlement.',
    choices: [
      {
        id: 'welcome',
        label: 'Welcome Them',
        description: 'Spend 10 food, gain workers (d6, max 3)',
        cost: { food: 10 },
        effects: { refugeesRoll: true, message: 'Refugees gratefully join your settlement.' }
      },
      {
        id: 'decline',
        label: 'Turn Away',
        description: 'Preserve resources, lose morale',
        effects: { morale: -5, message: 'The refugees depart with heavy hearts.' }
      }
    ]
  },
  { roll: [13, 13], name: 'Discovery',    icon: '💎', type: 'discovery',  description: 'Your settlers uncover hidden metal deposits!' },
  { roll: [14, 14], name: 'Scholar',      icon: '📖', type: 'scholar',    description: 'A learned scholar passes through, sharing knowledge.' },
  { roll: [15, 15], name: 'Festival',     icon: '🎉', type: 'festival',   description: 'Your people celebrate! No work today, but spirits soar.' },
  { roll: [16, 16], name: 'Breakthrough', icon: '💡', type: 'breakthrough', description: 'Your workshop achieves a breakthrough! (Fallback: discovery)' },
  { roll: [17, 18], name: 'Good Weather', icon: '🌈', type: 'weather',    description: 'Perfect conditions! All yields +2.' },
  { roll: [19, 19], name: 'Bounty',       icon: '✨', type: 'bounty',     description: "Double one task's output!" },
  { roll: [20, 20], name: 'Golden Age',   icon: '👑', type: 'golden_age', description: 'A golden age dawns! All tasks receive +3 today.' }
];

// Initial game state factory
export const createInitialState = () => ({
  phase: 'event',
  day: 1,
  food: CONFIG.STARTING_FOOD,
  wood: CONFIG.STARTING_WOOD,
  stone: CONFIG.STARTING_STONE,
  metal: CONFIG.STARTING_METAL,
  knowledge: CONFIG.STARTING_KNOWLEDGE,
  morale: CONFIG.STARTING_MORALE,
  techPoints: CONFIG.STARTING_TECH_POINTS,
  workers: Array.from({ length: CONFIG.STARTING_WORKERS }, (_, i) => ({
    id: i + 1,
    status: 'healthy',
    type: 'worker',
    joinedOn: 1
  })),
  buildings: [],
  buildingInProgress: null,
  currentEvent: null,
  eventRoll: null,
  raidScheduledForDay: null, // null or day number when raid should occur
  raidWarning: false, // Keep for backward compatibility
  assignments: {},
  rolls: {},
  pushes: {},
  tempWorker: null,
  bountyTask: null,
  combatResult: null,
  gameOver: false,
  gameWon: false,
  message: null,
  deathLog: [],
  choicesMade: {},
  raidsDefeated: 0,
  lastAssignments: {}, // Remember worker assignments between days
  futureRefugees: null, // NEW: { day: X, count: Y }
  futureRefugeesArrivalNotice: null, // NEW: notification object
  secondaryEvents: [], // NEW: births, arrivals, etc.
  buildingCostReduction: null, // NEW: 0.2 = 20% discount on next building
  pendingVictory: null        // Set when a special ending is newly met (claim prompt)
});
